import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, Col, Row, Form, Button, Table, Dropdown, Modal } from 'react-bootstrap';
import { RiMore2Fill, RiFileCopyLine, RiPencilLine, RiDeleteBinLine } from 'react-icons/ri';
import './preguntas-frecuentes.scss';
import { Breadcrumb } from 'react-bootstrap';
import { preguntasFrecuentesService } from '../../../services/preguntasFrecuentesService';
import { fqasTemasService } from '../../../services/fqasTemasService';
import { fqasRegisterService } from '../../../services/fqasRegisterService';
import { fqasDeleteService } from '../../../services/fqasDeleteService';
import { fqasEditService } from '../../../services/fqasEditService';

// Componente para el dropdown de sugerencias que se renderiza en un portal
const SugerenciasDropdown = ({ mostrar, sugerencias, posicion, onSeleccionar }) => {
  if (!mostrar || !sugerencias.length) return null;

  return createPortal(
    <div 
      className="sugerencias-temas-flotantes"
      style={{
        position: 'fixed',
        top: `${posicion.top}px`,
        left: `${posicion.left}px`,
        width: `${posicion.width}px`,
        zIndex: 9999
      }}
    >
      {sugerencias.map((tema, index) => (
        <div
          key={index}
          className="sugerencia-item"
          onClick={() => onSeleccionar(tema)}
        >
          {tema}
        </div>
      ))}
    </div>,
    document.body
  );
};

const PreguntasFrecuentes = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(6);
  const [loading, setLoading] = useState(false);
  const [faqData, setFaqData] = useState([]);
  const [proyectosEmpresa, setProyectosEmpresa] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [newResponse, setNewResponse] = useState({
    tipo: 'Pregunta frecuente',
    tema: '',
    pregunta: '',
    respuesta: ''
  });

  // Estados para las sugerencias de temas
  const [temasDisponibles, setTemasDisponibles] = useState([]);
  const [sugerenciasTemas, setSugerenciasTemas] = useState([]);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [posicionDropdown, setPosicionDropdown] = useState({ top: 0, left: 0, width: 0 });
  const temaInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Estados para las sugerencias de temas en el modal de edición
  const [sugerenciasTemasEdit, setSugerenciasTemasEdit] = useState([]);
  const [mostrarSugerenciasEdit, setMostrarSugerenciasEdit] = useState(false);
  const [posicionDropdownEdit, setPosicionDropdownEdit] = useState({ top: 0, left: 0, width: 0 });
  const temaInputEditRef = useRef(null);

  // Estados para el envío de datos
  const [enviando, setEnviando] = useState(false);
  const [mensajeEnvio, setMensajeEnvio] = useState('');

  // Estados para eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fqaToDelete, setFqaToDelete] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [mensajeEliminacion, setMensajeEliminacion] = useState('');
  const [coleccionName, setColeccionName] = useState('');
  const [idColeccion, setIdColeccion] = useState(null);

  // Estados para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  // Estados para edición
  const [showEditModal, setShowEditModal] = useState(false);
  const [fqaToEdit, setFqaToEdit] = useState(null);
  const [editResponse, setEditResponse] = useState({
    tipo: '',
    tema: '',
    pregunta: '',
    respuesta: ''
  });
  const [editando, setEditando] = useState(false);
  const [mensajeEdicion, setMensajeEdicion] = useState('');

  // Función para obtener el nombre del proyecto seleccionado
  const obtenerNombreProyecto = () => {
    if (!proyectoSeleccionado) return '';
    const proyecto = proyectosEmpresa.find(p => p.codigo_proyecto === proyectoSeleccionado);
    return proyecto ? proyecto.nombre_proyecto : '';
  };

  // Función para normalizar texto (quitar tildes y convertir a minúsculas)
  const normalizarTexto = (texto) => {
    return texto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Quitar tildes
      .trim();
  };

  // Función para parsear los filtros del buscador
  const parsearFiltros = (searchTerm) => {
    if (!searchTerm.trim()) return {};

    const filtros = {};
    const patrones = [
      { clave: 'tipo', regex: /#tipo\s*:\s*([^#]+)/i },
      { clave: 'tema', regex: /#tema\s*:\s*([^#]+)/i },
      { clave: 'pregunta', regex: /#pregunta\s*:\s*([^#]+)/i },
      { clave: 'respuesta', regex: /#respuesta\s*:\s*([^#]+)/i }
    ];

    patrones.forEach(({ clave, regex }) => {
      const match = searchTerm.match(regex);
      if (match) {
        filtros[clave] = normalizarTexto(match[1]);
      }
    });

    return filtros;
  };

  // Función para aplicar filtros a los datos
  const aplicarFiltros = (datos, filtros) => {
    if (Object.keys(filtros).length === 0) return datos;

    return datos.filter(item => {
      return Object.entries(filtros).every(([clave, valor]) => {
        const campo = normalizarTexto(item[clave] || '');
        return campo.includes(valor);
      });
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setMostrarSugerencias(false);
    setMensajeEnvio('');
    setEnviando(false);
    setNewResponse({
      tipo: 'Pregunta frecuente',
      tema: '',
      pregunta: '',
      respuesta: ''
    });
  };

  // Funciones para eliminación
  const handleDeleteClick = (fqa) => {
    setFqaToDelete(fqa);
    setShowDeleteModal(true);
    setMensajeEliminacion('');
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setFqaToDelete(null);
    setMensajeEliminacion('');
    setEliminando(false);
  };

  // Funciones para edición
  const handleEditClick = async (fqa) => {
    setFqaToEdit(fqa);
    setEditResponse({
      tipo: fqa.tipo || '',
      tema: fqa.tema || '',
      pregunta: fqa.pregunta || '',
      respuesta: fqa.respuesta || ''
    });
    setShowEditModal(true);
    setMensajeEdicion('');
    // Cargar temas cuando se abre el modal de edición
    await cargarTemas();
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setFqaToEdit(null);
    setMensajeEdicion('');
    setEditando(false);
    setEditResponse({
      tipo: '',
      tema: '',
      pregunta: '',
      respuesta: ''
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditResponse(prev => ({
      ...prev,
      [name]: value
    }));

    // Si es el campo tema, filtrar sugerencias para el modal de edición
    if (name === 'tema') {
      const sugerenciasFiltradas = temasDisponibles.filter(tema =>
        normalizarTexto(tema).includes(normalizarTexto(value))
      );
      setSugerenciasTemasEdit(sugerenciasFiltradas);
      
      // Recalcular posición cuando se muestran sugerencias
      if (sugerenciasFiltradas.length > 0) {
        calcularPosicionDropdownEdit();
      }
    }
  };

  const handleEditSubmit = async () => {
    if (!fqaToEdit) return;

    // Validar que todos los campos estén llenos
    if (!editResponse.tema.trim() || !editResponse.pregunta.trim() || !editResponse.respuesta.trim()) {
      setMensajeEdicion('❌ Error: Todos los campos son obligatorios');
      return;
    }

    try {
      setEditando(true);
      setMensajeEdicion('');

      console.log('✏️ [FQAs Edit] Editando FQA:', fqaToEdit);
      console.log('✏️ [FQAs Edit] Datos de edición:', editResponse);

      // Enviar al backend
      await fqasEditService.editFQA(null, fqaToEdit.id_vector, idColeccion, editResponse);
      
      setMensajeEdicion('Pregunta frecuente editada exitosamente');
      
      // Cerrar modal después de un breve delay
      setTimeout(() => {
        handleCloseEditModal();
        // Recargar las preguntas frecuentes para mostrar los cambios
        cargarPreguntasFrecuentes(proyectoSeleccionado);
      }, 1500);

    } catch (error) {
      console.error('❌ [FQAs Edit] Error al editar:', error);
      setMensajeEdicion(`❌ Error: ${error.message}`);
    } finally {
      setEditando(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!fqaToDelete) return;

    try {
      setEliminando(true);
      setMensajeEliminacion('');

      console.log('🗑️ [FQAs Delete] Eliminando FQA:', fqaToDelete);
      console.log('🗑️ [FQAs Delete] Coleccion Name:', coleccionName);
      console.log('🗑️ [FQAs Delete] ID Vector:', fqaToDelete.id_vector);

      // Enviar al backend
      await fqasDeleteService.deleteFQA(null, fqaToDelete.id_vector, coleccionName);
      
      setMensajeEliminacion('Pregunta frecuente eliminada exitosamente');
      
      // Cerrar modal después de un breve delay
      setTimeout(() => {
        handleCloseDeleteModal();
        // Recargar las preguntas frecuentes para actualizar la tabla
        cargarPreguntasFrecuentes(proyectoSeleccionado);
      }, 1500);

    } catch (error) {
      console.error('❌ [FQAs Delete] Error al eliminar:', error);
      setMensajeEliminacion(`❌ Error: ${error.message}`);
    } finally {
      setEliminando(false);
    }
  };
  
  const handleShowModal = async () => {
    setShowModal(true);
    // Cargar temas cuando se abre el modal
    await cargarTemas();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewResponse(prev => ({
      ...prev,
      [name]: value
    }));

    // Si es el campo tema, filtrar sugerencias
    if (name === 'tema') {
      filtrarSugerenciasTemas(value);
    }
  };

  // Función para manejar el cambio en el buscador
  const handleSearchChange = (e) => {
    const valor = e.target.value;
    setSearchTerm(valor);

    // Aplicar filtros en tiempo real
    const filtros = parsearFiltros(valor);
    const datosFiltrados = aplicarFiltros(faqData, filtros);
    setFilteredData(datosFiltrados);
  };

  const handleSubmit = async () => {
    // Validar que hay un proyecto seleccionado
    if (!proyectoSeleccionado) {
      setMensajeEnvio('❌ Error: Debe seleccionar un proyecto');
      return;
    }

    // Validar que todos los campos estén llenos
    if (!newResponse.tema.trim() || !newResponse.pregunta.trim() || !newResponse.respuesta.trim()) {
      setMensajeEnvio('❌ Error: Todos los campos son obligatorios');
      return;
    }

    try {
      setEnviando(true);
      setMensajeEnvio('');

      // Preparar los datos en el formato requerido
      const datosEnvio = {
        codigoProyecto: proyectoSeleccionado,
        questions: [
          {
            tipo: newResponse.tipo,
            tema: newResponse.tema.trim(),
            pregunta: newResponse.pregunta.trim(),
            respuesta: newResponse.respuesta.trim()
          }
        ]
      };

      console.log('📤 [FQAs Register] Enviando datos:', datosEnvio);

      // Enviar al backend
      const resultado = await fqasRegisterService.registerFQAs(null, datosEnvio);
      
      setMensajeEnvio('Pregunta frecuente creada exitosamente');
      
      // Cerrar modal después de un breve delay
      setTimeout(() => {
    handleCloseModal();
        // Recargar las preguntas frecuentes para mostrar la nueva
        cargarPreguntasFrecuentes(proyectoSeleccionado);
      }, 1500);

    } catch (error) {
      console.error('❌ [FQAs Register] Error al enviar:', error);
      setMensajeEnvio(`❌ Error: ${error.message}`);
    } finally {
      setEnviando(false);
    }
  };

  // Función para cargar temas desde la API
  const cargarTemas = async () => {
    try {
      const temas = await fqasTemasService.getTemas(null, proyectoSeleccionado);
      setTemasDisponibles(temas);
      console.log('🎯 [Temas] Temas cargados:', temas);
    } catch (error) {
      console.error('❌ [Temas] Error al cargar temas:', error);
    }
  };

  // Función para calcular la posición del dropdown
  const calcularPosicionDropdown = () => {
    if (temaInputRef.current) {
      const rect = temaInputRef.current.getBoundingClientRect();
      setPosicionDropdown({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Función para filtrar sugerencias de temas
  const filtrarSugerenciasTemas = (valor) => {
    if (valor.length === 0) {
      setSugerenciasTemas([]);
      setMostrarSugerencias(false);
      return;
    }

    const sugerencias = temasDisponibles.filter(tema =>
      tema.toLowerCase().includes(valor.toLowerCase())
    );
    
    setSugerenciasTemas(sugerencias);
    setMostrarSugerencias(sugerencias.length > 0);
    
    // Recalcular posición cuando se muestran sugerencias
    if (sugerencias.length > 0) {
      calcularPosicionDropdown();
    }
  };

  // Función para seleccionar una sugerencia
  const seleccionarSugerencia = (tema) => {
    setNewResponse(prev => ({
      ...prev,
      tema: tema
    }));
    setMostrarSugerencias(false);
  };

  // Función para calcular la posición del dropdown en el modal de edición
  const calcularPosicionDropdownEdit = () => {
    if (temaInputEditRef.current) {
      const rect = temaInputEditRef.current.getBoundingClientRect();
      setPosicionDropdownEdit({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Función para seleccionar una sugerencia en el modal de edición
  const seleccionarSugerenciaEdit = (tema) => {
    setEditResponse(prev => ({
      ...prev,
      tema: tema
    }));
    setMostrarSugerenciasEdit(false);
  };

  // Función para cargar las preguntas frecuentes desde la API
  const cargarPreguntasFrecuentes = async (codigoProyecto = null) => {
    try {
      setLoading(true);
      const data = await preguntasFrecuentesService.getPreguntasFrecuentes(null, codigoProyecto);
      console.log('📋 [Preguntas Frecuentes] Datos cargados:', data);
      
      // Establecer proyectos y FQAs
      setProyectosEmpresa(data.proyectosEmpresa);
      setFaqData(data.coleccion.fqas);
      setColeccionName(data.coleccion.coleccion_name); // Store collection name
      setIdColeccion(data.coleccion.id_coleccion); // Store collection ID
      setFilteredData(data.coleccion.fqas); // Inicializar datos filtrados
      
      // Si es la primera carga y no hay proyecto seleccionado, establecer el proyecto por defecto
      if (!proyectoSeleccionado && data.coleccion.codigo_proyecto) {
        // Usar el codigo_proyecto que viene en el objeto coleccion
        setProyectoSeleccionado(data.coleccion.codigo_proyecto);
      }
    } catch (error) {
      console.error('❌ [Preguntas Frecuentes] Error al cargar datos:', error);
      // En caso de error, limpiar los datos y mostrar tabla vacía
      setFaqData([]);
      setProyectosEmpresa([]);
      setProyectoSeleccionado(null);
      setColeccionName('');
      setIdColeccion(null);
      setFilteredData([]);
      setSearchTerm('');
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar el cambio de proyecto
  const handleProyectoChange = (e) => {
    const valor = e.target.value;
    
    if (valor === '') {
      // Si selecciona "Seleccionar proyecto", limpiar la tabla completamente
      setProyectoSeleccionado(null);
      setFaqData([]);
      setFilteredData([]);
      setSearchTerm('');
      setCurrentPage(1);
      setColeccionName('');
      setIdColeccion(null);
      return;
    }
    
    const codigoProyecto = parseInt(valor);
    setProyectoSeleccionado(codigoProyecto);
    setCurrentPage(1); // Resetear a la primera página
    cargarPreguntasFrecuentes(codigoProyecto);
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarPreguntasFrecuentes();
  }, []);

  // Resetear página cuando cambie el término de búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);


  // Calcular el total de páginas
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  // Obtener los registros de la página actual
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  return (
    <div className="preguntas-frecuentes-container">
      <div className="breadcrumb-header">
        <Breadcrumb>
          <Breadcrumb.Item>Fine - Tunning</Breadcrumb.Item>
          <Breadcrumb.Item active>Respuestas frecuentes</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>
              <h3><strong>Respuestas frecuentes</strong></h3>
              <div className="header-right">
                <Button variant="primary" onClick={handleShowModal}>Nueva respuesta</Button>
              </div>
            </Card.Header>
            <Card.Body>
               <div className="faq-description">
                <strong><h4>Lista de respuestas frecuentes</h4></strong>
              </div>

              <div className="faq-description">
                <p>En esta sección podrás personalizar las preguntas y objeciones frecuentes del negocio.</p>
              </div>
              
            <div className="search-container d-flex gap-3">
                <Form.Control 
                  type="text" 
                  placeholder="Buscar por: #tipo:... #tema:... #pregunta:... #respuesta:... Ejemplo: #tema:visitas #pregunta:¿dónde está la oficina?"
                  className="search-input flex-grow-1"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              <Form.Select
                value={proyectoSeleccionado || ''}
                onChange={handleProyectoChange}
                className="project-selector"
                style={{ minWidth: '150px', maxWidth: '180px', fontSize: '12px' }}
              >
                <option value="">Seleccionar proyecto</option>
                {proyectosEmpresa.map((proyecto) => (
                  <option key={proyecto.codigo_proyecto} value={proyecto.codigo_proyecto}>
                    {proyecto.nombre_proyecto}
                  </option>
                ))}
              </Form.Select>
              </div>

              <div className="table-responsive">
                <Table className="faq-table">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Tema</th>
                      <th>Pregunta u objeción</th>
                      <th>Respuesta</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <div className="d-flex align-items-center justify-content-center">
                            <div className="spinner-border text-primary me-2" role="status">
                              <span className="visually-hidden">Cargando...</span>
                            </div>
                            <span>Cargando preguntas frecuentes...</span>
                          </div>
                        </td>
                      </tr>
                    ) : filteredData.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          <div className="text-muted">
                            <i className="fe fe-inbox me-2"></i>
                            {searchTerm ? 'No se encontraron resultados para la búsqueda' : 'No hay preguntas frecuentes disponibles'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      getCurrentPageData().map((item, index) => (
                      <tr key={index}>
                        <td>{item.tipo}</td>
                          <td>{item.tema}</td>
                        <td>{item.pregunta}</td>
                        <td>{item.respuesta}</td>
                        <td>
                          <Dropdown align="end">
                            <Dropdown.Toggle as="span" variant="link" className="icono-dropdown p-0">
                              <RiMore2Fill className="text-muted" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-end">
                              {/* <Dropdown.Item className="d-flex align-items-center">
                                <RiFileCopyLine className="me-2" /> Duplicar
                              </Dropdown.Item> */}
                              <Dropdown.Item 
                                className="d-flex align-items-center"
                                onClick={() => handleEditClick(item)}
                              >
                                <RiPencilLine className="me-2" /> Editar
                              </Dropdown.Item>
                              <Dropdown.Item 
                                className="d-flex align-items-center text-danger"
                                onClick={() => handleDeleteClick(item)}
                              >
                                <RiDeleteBinLine className="me-2" /> Eliminar
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="pagination-container">
                <div className="records-per-page">
                  <span>Registros por página:</span>
                  <Form.Select 
                    value={recordsPerPage}
                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                    className="records-select"
                  >
                    <option value={6}>6</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Form.Select>
                </div>
                <div className="pagination-controls">
                  <Button 
                    variant="link" 
                    className="page-nav"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </Button>
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    // Mostrar siempre las primeras 6 páginas, la última y las páginas alrededor de la actual
                    if (
                      pageNumber <= 6 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={pageNumber}
                          variant="link"
                          className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </Button>
                      );
                    } else if (
                      (pageNumber === 7 && totalPages > 7) ||
                      (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                    ) {
                      return <span key={pageNumber} className="page-ellipsis">...</span>;
                    }
                    return null;
                  })}
                  <Button 
                    variant="link" 
                    className="page-nav"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal de Nueva Respuesta */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Nueva respuesta</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="mb-4">
              <Form.Label className="form-label-bold">Tipo y descripción de la respuesta</Form.Label>
              
              {/* Campo de proyecto seleccionado (solo lectura) */}
              <Form.Group className="mb-3">
                <Form.Label className="form-label-light">Proyecto</Form.Label>
                <Form.Control
                  type="text"
                  value={obtenerNombreProyecto()}
                  readOnly
                  className="form-control-custom"
                  style={{
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    cursor: 'not-allowed'
                  }}
                  placeholder="Ningún proyecto seleccionado"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="form-label-light">Tipo</Form.Label>
                <Form.Select 
                  name="tipo"
                  value={newResponse.tipo}
                  onChange={handleInputChange}
                  className="form-select-custom"
                >
                  <option>Pregunta frecuente</option>
                  <option>Objeción frecuente</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="form-label-light">Tema</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    ref={temaInputRef}
                    type="text"
                    placeholder="Escribe el tema de la pregunta u objeción"
                    name="tema"
                    value={newResponse.tema}
                    onChange={handleInputChange}
                    onFocus={() => {
                      calcularPosicionDropdown();
                      if (sugerenciasTemas.length > 0) {
                        setMostrarSugerencias(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay para permitir click en sugerencias
                      setTimeout(() => setMostrarSugerencias(false), 200);
                    }}
                    className="form-control-custom"
                    autoComplete="off"
                  />
                </div>
              </Form.Group>
              
              {/* Dropdown de sugerencias renderizado en portal */}
              <SugerenciasDropdown
                mostrar={mostrarSugerencias}
                sugerencias={sugerenciasTemas}
                posicion={posicionDropdown}
                onSeleccionar={seleccionarSugerencia}
              />

              <Form.Group className="mb-3">
                <Form.Label className="form-label-light">Pregunta u objeción</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Escribe una pregunta u objeción"
                  name="pregunta"
                  value={newResponse.pregunta}
                  onChange={handleInputChange}
                  className="form-control-custom"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="form-label-light">Respuesta</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Escribe una respuesta a la pregunta u objeción"
                  name="respuesta"
                  value={newResponse.respuesta}
                  onChange={handleInputChange}
                  className="form-control-custom"
                />
              </Form.Group>
            </div>

            {/* Mensaje de estado */}
            {mensajeEnvio && (
              <div className={`alert ${mensajeEnvio.includes('') ? 'alert-success' : 'alert-danger'} mb-3`}>
                {mensajeEnvio}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={handleCloseModal}
            disabled={enviando}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={enviando}
          >
            {enviando ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creando...
              </>
            ) : (
              'Crear respuesta'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas eliminar esta pregunta frecuente?</p>
          {fqaToDelete && (
            <div className="bg-light p-3 rounded">
              <strong>Tema:</strong> {fqaToDelete.tema}<br/>
              <strong>Pregunta:</strong> {fqaToDelete.pregunta}<br/>
              <strong>Respuesta:</strong> {fqaToDelete.respuesta}
            </div>
          )}
          
          {/* Mensaje de estado */}
          {mensajeEliminacion && (
            <div className={`alert ${mensajeEliminacion.includes('') ? 'alert-success' : 'alert-danger'} mb-3 mt-3`}>
              {mensajeEliminacion}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={handleCloseDeleteModal}
            disabled={eliminando}
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmDelete}
            disabled={eliminando}
          >
            {eliminando ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Eliminando...
              </>
            ) : (
              'Eliminar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de edición */}
      <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar respuesta frecuente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="form-label-light">Proyecto</Form.Label>
              <Form.Control
                type="text"
                value={obtenerNombreProyecto()}
                readOnly
                className="form-control-custom"
                style={{
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  cursor: 'not-allowed'
                }}
                placeholder="Ningún proyecto seleccionado"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="form-label-light">Tipo</Form.Label>
              <Form.Select
                name="tipo"
                value={editResponse.tipo}
                onChange={handleEditInputChange}
                className="form-control-custom"
              >
                <option value="Pregunta frecuente">Pregunta frecuente</option>
                <option value="Objeción">Objeción</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="form-label-light">Tema</Form.Label>
              <div className="position-relative">
                <Form.Control
                  ref={temaInputEditRef}
                  type="text"
                  name="tema"
                  value={editResponse.tema}
                  onChange={handleEditInputChange}
                  onFocus={() => {
                    setMostrarSugerenciasEdit(true);
                    calcularPosicionDropdownEdit();
                  }}
                  onBlur={() => {
                    setTimeout(() => setMostrarSugerenciasEdit(false), 200);
                  }}
                  autoComplete="off"
                  className="form-control-custom"
                  placeholder="Ingrese el tema"
                />
                <SugerenciasDropdown
                  mostrar={mostrarSugerenciasEdit}
                  sugerencias={sugerenciasTemasEdit}
                  posicion={posicionDropdownEdit}
                  onSeleccionar={seleccionarSugerenciaEdit}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="form-label-light">Pregunta u objeción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="pregunta"
                value={editResponse.pregunta}
                onChange={handleEditInputChange}
                className="form-control-custom"
                placeholder="Ingrese la pregunta u objeción"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="form-label-light">Respuesta</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="respuesta"
                value={editResponse.respuesta}
                onChange={handleEditInputChange}
                className="form-control-custom"
                placeholder="Ingrese la respuesta"
              />
            </Form.Group>

            {/* Mensaje de estado */}
            {mensajeEdicion && (
              <div className={`alert ${mensajeEdicion.includes('') ? 'alert-success' : 'alert-danger'} mb-3`}>
                {mensajeEdicion}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={handleCloseEditModal}
            disabled={editando}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleEditSubmit}
            disabled={editando}
          >
            {editando ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Editando...
              </>
            ) : (
              'Guardar cambios'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PreguntasFrecuentes;
